import React, { useState, useEffect, useRef } from 'react';

const MultiSelect = (props) => {
    const labelClass = props.error
        ? "block text-sm font-medium text-red-700 dark:text-red-500"
        : "block text-sm font-medium text-gray-900 dark:text-white";
    const [selectedOptions, setSelectedOptions] = useState(props.selectedOptions || []);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    const dropdownRef = useRef(null);
    const optionsListRef = useRef(null);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    useEffect(() => {
        if (props.onChangeOptions) {
            props.onChangeOptions(selectedOptions)
        }
    }, [selectedOptions]);

    useEffect(() => {
        setSelectedOptions(props.selectedOptions || []);
    }, [props.selectedOptions]);

    useEffect(() => {
        if (isDropdownOpen && optionsListRef.current) {
            const checkScrollable = () => {
                const element = optionsListRef.current;
                setShowScrollIndicator(element.scrollHeight > element.clientHeight);
            };
            
            checkScrollable();
            // Re-check if window is resized
            window.addEventListener('resize', checkScrollable);
            return () => window.removeEventListener('resize', checkScrollable);
        }
    }, [isDropdownOpen, props.options]);

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleOptionClick = (option) => {
        setSelectedOptions((prevSelected) => {
            if (prevSelected.includes(option.id)) {
                return prevSelected.filter((id) => id !== option.id);
            } else {
                return [...prevSelected, option.id];
            }
        });
    };

    const handleDeselectAll = (e) => {
        e.stopPropagation();
        setSelectedOptions([]);
    };

    const handleRemoveChip = (optionId, e) => {
        e.stopPropagation();
        setSelectedOptions(prevSelected => prevSelected.filter(id => id !== optionId));
    };

    const isSelected = (optionId) => selectedOptions.includes(optionId);

    // Find the label for a selected option ID
    const getOptionLabel = (optionId) => {
        const option = props.options.find(opt => opt.id === optionId);
        return option ? option.label : optionId;
    };

    return (
        <div className={props.rootClass}>
            <div ref={dropdownRef} className="flex flex-col gap-2">

                <label htmlFor={props.id} className={labelClass}>
                    {props.label}
                    {props.optional &&
                        <span className="pl-2 text-gray-400 italic">- Optional</span>
                    }
                </label>
                <div
                    className="border border-gray-300 bg-gray-50 rounded-lg text-sm py-2 px-3 cursor-pointer min-h-[42px]"
                    onClick={toggleDropdown}
                >
                    {selectedOptions.length > 0 ? (
                        <div className="flex flex-wrap gap-2 items-center">
                            {selectedOptions.map(optionId => (
                                <div 
                                    key={optionId} 
                                    className="flex items-center bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-xs"
                                >
                                    {getOptionLabel(optionId)}
                                    <button 
                                        className="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                                        onClick={(e) => handleRemoveChip(optionId, e)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <span className="text-gray-500">Select Options</span>
                    )}
                </div>
                {isDropdownOpen && (
                    <div className="absolute mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                        {selectedOptions.length > 0 && (
                            <div 
                                className="py-2 px-4 text-left border-b border-gray-200"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button 
                                    className="text-xs text-red-600 hover:text-red-800 focus:outline-none px-2 py-1"
                                    onClick={handleDeselectAll}
                                >
                                    Deselect All
                                </button>
                            </div>
                        )}
                        <ul 
                            ref={optionsListRef}
                            className="max-h-60 overflow-y-auto"
                            style={{ 
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#9ca3af #f3f4f6'
                            }}
                        >
                            {props.options.map((option) => (
                                <li
                                    key={option.id}
                                    className={`flex flex-row items-center p-2 cursor-pointer hover:bg-gray-200 ${
                                        isSelected(option.id) ? 'bg-gray-100' : ''
                                    }`}
                                    onClick={() => handleOptionClick(option)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected(option.id)}
                                        readOnly
                                        className="mr-2 rounded-md"
                                    />
                                    <p className="text-base text-gray-900">{option.label}</p>
                                </li>
                            ))}
                        </ul>
                        {showScrollIndicator && (
                            <div className="text-center py-1 border-t border-gray-200 text-xs text-gray-500">
                                Scroll for more options
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MultiSelect;
